import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BuyAgainCard,
  BuyAgainLiveCard,
  BuyAgainState,
} from "./types";
import {
  Order,
  OrdersResponse,
  CustomerDeliveryStatus,
  PaymentStatus,
} from "../custmoer_orders/types";
import { getCookie } from "../../utility/getCookie";

// ─── API base URLs ────────────────────────────────────────────────────────────
// Orders live in paymentservice; live product data lives in product-service.
const PAYMENT_API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

const CATALOG_API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies + Bearer token for the paymentservice auth.
const paymentApi = axios.create({
  baseURL: PAYMENT_API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});
paymentApi.interceptors.request.use((config) => {
  const token = getCookie("access_token");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

// Product-service is public for catalog reads — no auth header needed.
const catalogApi = axios.create({ baseURL: CATALOG_API_URL });

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Days between a past ISO date and now (0 if invalid). */
const daysAgo = (isoDate: string): number => {
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24)));
};

/**
 * An order is "complete" for reorder purposes if it was delivered OR the
 * payment succeeded. Mirrors the eligibility rule used by the OrdersPage
 * "Buy it again" sidebar so both surfaces list the same products.
 */
const isCompletedOrder = (order: Order): boolean => {
  const pay = String(order.paymentStatus ?? "").toUpperCase();
  return (
    order.deliveryStatus === CustomerDeliveryStatus.DELIVERED ||
    pay === PaymentStatus.COMPLETED ||
    pay === "SUCCESS"
  );
};

/** Extract a unique, most-recent-first product list from completed orders. */
function extractUniqueProducts(orders: Order[]): BuyAgainCard[] {
  // Group by productId — keep the most recent order for each product and
  // count total purchases across all orders.
  const map = new Map<string, BuyAgainCard>();
  for (const order of orders) {
    if (!isCompletedOrder(order)) continue;
    const orderDate = order.orderDate;
    const orderNo = order.orderNo || order.orderNumber || "";
    for (const item of order.productDetails ?? []) {
      const productId = item.productId;
      if (!productId) continue;
      const existing = map.get(productId);
      if (existing) {
        existing.purchaseCount += 1;
        // Keep the most recent order's metadata
        if (new Date(orderDate) > new Date(existing.lastOrderDate)) {
          existing.lastOrderDate = orderDate;
          existing.lastOrderNo = orderNo;
          existing.orderPrice = item.unitPrice || item.totalPrice || existing.orderPrice;
        }
      } else {
        map.set(productId, {
          productId,
          title: item.productName || "Product",
          orderImage: item.productImage || "/assets/images/logos/salarlogo.png",
          orderPrice: item.unitPrice || item.totalPrice || 0,
          purchasedDaysAgo: daysAgo(orderDate),
          purchaseCount: 1,
          lastOrderDate: orderDate,
          lastOrderNo: orderNo,
          orderCategory: item.productType || "Product",
          live: null,
        });
      }
    }
  }
  // Sort by most recently purchased first
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.lastOrderDate).getTime() - new Date(a.lastOrderDate).getTime(),
  );
}

/** Fetch live catalog data for one product by its ObjectId or SSIN. */
async function fetchLiveCard(productId: string): Promise<BuyAgainLiveCard | null> {
  try {
    const res = await catalogApi.get(`/e-commerce/catalog/products/${productId}`);
    const p = res.data?.data;
    if (!p) return null;
    const fo = p.featuredOffer ?? {};
    const cat = p.categoryHierarchy ?? {};
    return {
      mainImage: p.mainImage ?? "",
      description: p.description ?? "",
      brandName: p.brand?.name ?? "",
      price: Number(fo.finalPrice ?? 0),
      mrp: Number(fo.unitPrice ?? fo.mrp ?? fo.finalPrice ?? 0),
      discountPercent: Number(fo.savingsPercent ?? fo.discountPercent ?? 0),
      rating: p.reviews?.averageRating ?? 0,
      reviewCount: p.reviews?.totalReviews ?? 0,
      dealLabel: fo.deal ? `${fo.deal.discountPercent}% off` : null,
      couponLabel: fo.coupon?.title ?? null,
      freeDelivery: Boolean(fo.freeDelivery),
      fastDelivery: Boolean(fo.fastDelivery),
      nextDayDelivery: Boolean(fo.nextDayDelivery),
      stockStatus: p.stockStatus ?? "in_stock",
      colors: Array.isArray(p.colors) ? p.colors : [],
      category: cat.childCategory || cat.subCategory || cat.category || "",
    };
  } catch {
    // Product may be delisted — return null so the card still shows with order data
    return null;
  }
}

/** Enrich cards in batches to parallelize without overwhelming the API. */
async function enrichCards(
  cards: BuyAgainCard[],
  onProgress: (done: number, total: number) => void,
): Promise<BuyAgainCard[]> {
  const batchSize = 8;
  const enriched: BuyAgainCard[] = [...cards];
  let done = 0;
  for (let i = 0; i < enriched.length; i += batchSize) {
    const batch = enriched.slice(i, i + batchSize);
    const liveResults = await Promise.all(
      batch.map((card) => fetchLiveCard(card.productId)),
    );
    liveResults.forEach((live, idx) => {
      enriched[i + idx].live = live;
    });
    done = Math.min(done + batch.length, enriched.length);
    onProgress(done, enriched.length);
  }
  return enriched;
}

// ─── Async Thunks ────────────────────────────────────────────────────────────

/**
 * Fetch all delivered orders, extract unique products, then enrich each with
 * live catalog data. The thunk dispatches incremental updates via the
 * `pending`/`fulfilled` handlers so the UI can show order data immediately
 * and update cards as enrichment completes.
 */
export const fetchBuyAgainProducts = createAsyncThunk<
  BuyAgainCard[],
  void,
  { rejectValue: string }
>("buyAgain/fetchBuyAgainProducts", async (_, { dispatch, rejectWithValue }) => {
  try {
    // 1. Paginate all successfully-paid orders (page through until exhausted).
    //    paymentStatus=COMPLETED is broader than deliveryStatus=DELIVERED — it
    //    includes paid orders still in transit, matching the OrdersPage sidebar.
    const allOrders: Order[] = [];
    let page = 1;
    const limit = 50;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const res = await paymentApi.get<OrdersResponse>("/customer/orders", {
        params: { page, limit, paymentStatus: PaymentStatus.COMPLETED },
      });
      const orders = res.data?.data?.orders ?? [];
      allOrders.push(...orders);
      const totalPages = res.data?.data?.pagination?.totalPages ?? 1;
      if (page >= totalPages) break;
      page += 1;
    }

    // 2. Extract unique purchased products
    const cards = extractUniqueProducts(allOrders);
    if (cards.length === 0) return [];

    // 3. Set cards with null live data immediately (UI can render order data)
    dispatch(buyAgainSlice.actions.setCards(cards));

    // 4. Enrich with live catalog data in batches
    const enriched = await enrichCards(cards, (done, total) => {
      dispatch(buyAgainSlice.actions.setEnrichProgress({ done, total }));
    });
    return enriched;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to load buy again products",
    );
  }
});

/**
 * Re-fetch live data for a single product (e.g. after the customer reorders
 * one and we want to refresh its stock/price). Updates one card in place.
 */
export const refreshBuyAgainCard = createAsyncThunk<
  { productId: string; live: BuyAgainLiveCard | null },
  string,
  { rejectValue: string }
>("buyAgain/refreshBuyAgainCard", async (productId, { rejectWithValue }) => {
  try {
    const live = await fetchLiveCard(productId);
    return { productId, live };
  } catch {
    return rejectWithValue("Failed to refresh product");
  }
});

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: BuyAgainState = {
  cards: [],
  loading: false,
  enriching: false,
  enrichProgress: { done: 0, total: 0 },
  error: null,
  lastFetchedAt: null,
};

const buyAgainSlice = createSlice({
  name: "buyAgain",
  initialState,
  reducers: {
    /** Set cards immediately after orders are fetched (before enrichment). */
    setCards: (state, action: PayloadAction<BuyAgainCard[]>) => {
      state.cards = action.payload;
    },
    /** Update enrichment progress for the UI progress bar. */
    setEnrichProgress: (
      state,
      action: PayloadAction<{ done: number; total: number }>,
    ) => {
      state.enrichProgress = action.payload;
    },
    /** Clear cache so the next mount re-fetches. */
    invalidate: (state) => {
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuyAgainProducts.pending, (state) => {
        state.loading = true;
        state.enriching = false;
        state.error = null;
        state.enrichProgress = { done: 0, total: 0 };
      })
      .addCase(fetchBuyAgainProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.enriching = false;
        state.cards = action.payload;
        state.lastFetchedAt = Date.now();
        state.enrichProgress = { done: action.payload.length, total: action.payload.length };
      })
      .addCase(fetchBuyAgainProducts.rejected, (state, action) => {
        state.loading = false;
        state.enriching = false;
        state.error = action.payload || "Failed to load buy again products";
      })
      .addCase(refreshBuyAgainCard.fulfilled, (state, action) => {
        const { productId, live } = action.payload;
        const card = state.cards.find((c) => c.productId === productId);
        if (card) card.live = live;
      });
  },
});

export const { setCards, setEnrichProgress, invalidate } = buyAgainSlice.actions;
export default buyAgainSlice.reducer;
